import React, { useEffect, useState } from "react";
import {
  Button,
  Group,
  Modal,
  Select,
  SimpleGrid,
  Text,
  createStyles,
} from "@mantine/core";

import { useSelector } from "react-redux";
import { renameFile } from "../../services/Document";
import { showInfo } from "../../utils/index";

const useStyles = createStyles((theme) => ({
  myfont: {
    fontFamily: "Inter",
    overflow: "visible",
  },
  mymodal: {
    width: 818,
    overflow: "visible",
    "& .mantine-Paper-root": {
      overflow: "initial !important",
    },
    "& .mantine-Modal-inner": {
      padding: "0px !important",
    },
    "@media (max-width:400px)": {
      width: "auto",
    },
  },
  mwd: {
    width: "100%",
  },
}));

const findFolderPath = (list, folderId) => {
  for (const folder of list || []) {
    const id = folder?._id || folder?.id;
    if (id === folderId) return [folder];
    if (folder?.folders?.length) {
      const nested = findFolderPath(folder.folders, folderId);
      if (nested) return [folder, ...nested];
    }
  }
  return null;
};

// The folder tree is flattened into one list so every folder — nested ones
// included — is pickable in a single step, each labelled with its full path.
const flattenFolders = (list, trail = []) => {
  const options = [];
  (list || []).forEach((folder) => {
    const id = folder?._id || folder?.id;
    if (!id) return;
    const path = [...trail, folder?.name];
    options.push({ value: id, label: path.join(" / ") });
    options.push(...flattenFolders(folder?.folders, path));
  });
  // A folder reachable from two branches would otherwise appear twice.
  return options.filter((opt, idx) => options.findIndex((o) => o.value === opt.value) === idx);
};

const MoveFileModal = ({ opened, onclose, title, data, client, currentFolderId }) => {
  const { classes } = useStyles();
  const { renameFileLoading, folders } = useSelector((state) => state.document);

  const [selectedId, setSelectedId] = useState(null);
  const [currentLocationPath, setCurrentLocationPath] = useState([]);

  const folderOptions = flattenFolders(folders);

  useEffect(() => {
    if (opened) {
      const fileFolderId = data?.folder?._id || data?.folder?.id || null;
      const existingPath = fileFolderId
        ? findFolderPath(folders, fileFolderId) ||
          [{ _id: fileFolderId, name: data?.folder?.name }]
        : null;
      setSelectedId(fileFolderId);
      setCurrentLocationPath(existingPath || []);
    }
  }, [opened]);

  const selectedLabel = folderOptions.find((opt) => opt.value === selectedId)?.label;

  const resetForm = () => {
    setSelectedId(null);
    onclose();
  };

  const onSave = async () => {
    try {
      if (data) {
        let payload = {
          name: data?.name,
          folder: selectedId || null,
          file: data?.id,
        };
        // a rejected move (e.g. a destination that is not this record's folder)
        // already surfaces an error toast — keep the modal open so the pick can be redone
        const moved = await renameFile(payload, currentFolderId, client, "client");
        if (!moved) return;

        const destination = selectedId
          ? `"${selectedLabel || "the selected folder"}"`
          : "All Documents";
        resetForm();
        showInfo(`"${data?.name}" moved to ${destination}`);
        return;
      }
      resetForm();
    } catch (error) {
      console.error("Error moving file:", error.message);
    }
  };

  return (
    <div key={data?._id} style={{ overflowX: "auto" }}>
      <div style={{ minWidth: "600px" }}>
        <Modal
          className={classes.mymodal}
          opened={opened}
          size={"xl"}
          onClose={() => {
            onclose();
          }}
          shadow="sm"
          radius={"lg"}
          zIndex={1000}
          title={
            <Group>
              <img src="/assets/images/addfac.png" alt="" />
              <Text sx={{ fontFamily: "Inter", fontSize: 22, fontWeight: 600 }}>
                {title}
              </Text>
            </Group>
          }
          centered
        >
          <Text size="sm" mb={10} color="dimmed">
            Currently in:{" "}
            <Text span fw={600} c="dark">
              {currentLocationPath?.length
                ? `All Documents / ${currentLocationPath.map((f) => f?.name).join(" / ")}`
                : "All Documents"}
            </Text>
          </Text>
          <SimpleGrid
            cols={1}
            className={classes.mwd}
            sx={{
              marginTop: 10,
              paddingBottom: 30,
            }}
          >
            <Select
              className={classes.myfont}
              label="Move to"
              size="md"
              placeholder="All Documents"
              data={folderOptions}
              value={selectedId}
              onChange={setSelectedId}
              clearable
              searchable
              nothingFound="No folders found"
              // rendered in a portal above the modal, otherwise the list opens
              // inside the modal body and covers the field it belongs to
              withinPortal
              zIndex={1500}
              dropdownPosition="bottom"
              maxDropdownHeight={260}
            />
          </SimpleGrid>

          <Text size="xs" color="dimmed" mb={10}>
            {selectedId
              ? `File will be moved to "${selectedLabel || ""}"`
              : "File will be moved to All Documents (no folder)"}
          </Text>

          <Button
            color="yellow"
            sx={{ marginRight: "auto", width: 122, fontFamily: "Inter" }}
            onClick={onSave}
            loading={renameFileLoading}
          >
            Move
          </Button>
        </Modal>
      </div>
    </div>
  );
};

export default MoveFileModal;
